/**
 * Attributes the table stamps on its structural parts so an outside measurer can
 * find them through a single ref to the root.
 *
 * The table itself attaches nothing else and reads none of these back — it just
 * labels what it renders. Anything that needs to know how tall the chrome is, or
 * how tall a row is, walks in from the root and looks these up.
 */
export const TANSTACK_TABLE_PART = {
  root: 'data-tt-root',
  toolbar: 'data-tt-toolbar',
  head: 'data-tt-head',
  body: 'data-tt-body',
  row: 'data-tt-row',
  pagination: 'data-tt-pagination',
} as const;

export type TanstackTablePart = keyof typeof TANSTACK_TABLE_PART;

/** `[data-tt-row]` — ready to hand to `querySelector`. */
export function partSelector(part: TanstackTablePart): string {
  return `[${TANSTACK_TABLE_PART[part]}]`;
}

/**
 * True when `element` belongs to `root` rather than to a table nested inside it
 * — a detail panel may well render another table, and its parts must not be
 * mistaken for the outer one's. `closest` walks up to the nearest enclosing
 * root, which for a nested part is that table, not this one.
 */
function isOwnedBy(element: Element, root: Element): boolean {
  return element.closest(partSelector('root')) === root;
}

/** The root's own instance of a part, ignoring any nested table's. */
export function findPart(root: HTMLElement | null, part: TanstackTablePart): HTMLElement | null {
  if (!root) return null;
  const matches = root.querySelectorAll<HTMLElement>(partSelector(part));
  for (let index = 0; index < matches.length; index++) {
    if (isOwnedBy(matches[index], root)) return matches[index];
  }
  return null;
}

/** All of the root's own instances of a part, ignoring any nested table's. */
export function findParts(root: HTMLElement | null, part: TanstackTablePart): HTMLElement[] {
  if (!root) return [];
  return Array.from(root.querySelectorAll<HTMLElement>(partSelector(part))).filter(
    (element: HTMLElement) => isOwnedBy(element, root)
  );
}
